import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchDepotApi,
  fetchDepotPositionWerteApi,
  addDepotPositionApi,
  deleteDepotPositionApi,
  addDepotTransaktionApi,
  setDepotGruppeApi,
  createDepotGruppeApi,
  renameDepotGruppeApi,
  deleteDepotGruppeApi,
  reorderDepotGruppenApi,
} from './aktienApi';

/**
 * Redux-Slice fuer das Nutzer-Depot. Die CRUD-Endpunkte liefern die schnelle
 * Basisansicht ({ positionen, gruppen }, nur Kaufdaten). Die angereicherten Werte
 * (Kurs, Urteil, Dividende, Perf) werden pro Position ueber fetchPositionWerte
 * nachgeladen, damit die Struktur sofort steht.
 */

export const fetchDepot = createAsyncThunk('depot/fetch', async () => {
  return (await fetchDepotApi()) ?? { positionen: [], gruppen: [] };
});

export const fetchPositionWerte = createAsyncThunk('depot/fetchWerte', async (isin) => {
  return { isin, position: await fetchDepotPositionWerteApi(isin) };
});

export const addPosition = createAsyncThunk('depot/addPosition', async (position) => {
  return await addDepotPositionApi(position);
});

export const removePosition = createAsyncThunk('depot/removePosition', async (isin) => {
  return await deleteDepotPositionApi(isin);
});

export const addTransaktion = createAsyncThunk('depot/addTransaktion', async ({ isin, transaktion }) => {
  return await addDepotTransaktionApi(isin, transaktion);
});

export const setGruppe = createAsyncThunk('depot/setGruppe', async ({ isin, gruppeUuid }) => {
  return await setDepotGruppeApi(isin, gruppeUuid);
});

export const createGruppe = createAsyncThunk('depot/createGruppe', async (name) => {
  return await createDepotGruppeApi(name);
});

export const renameGruppe = createAsyncThunk('depot/renameGruppe', async ({ uuid, name }) => {
  return await renameDepotGruppeApi(uuid, name);
});

export const deleteGruppe = createAsyncThunk('depot/deleteGruppe', async (uuid) => {
  return await deleteDepotGruppeApi(uuid);
});

export const reorderGruppen = createAsyncThunk('depot/reorderGruppen', async (reihenfolge) => {
  return await reorderDepotGruppenApi(reihenfolge);
});

const initialState = {
  positionen: [],
  gruppen: [],
  status: 'idle', // idle | loading | succeeded | failed
  error: null,
};

// Angereicherte Felder, die pro Position separat nachgeladen werden.
const WERTE_FELDER = [
  'currency', 'anzeigeWaehrung', 'aktuellPreis', 'aenderungProz',
  'gesamtUrteilAmpel', 'gesamtUrteilScore', 'aktuellerWert', 'gewinnAbsolut',
  'gewinnProz', 'dividendeBetrag', 'dividendeRendite', 'naechsterExTag',
  'naechsterZahltag', 'perf30Tage', 'perf200Tage',
];

/**
 * Uebernimmt die Basisansicht und erhaelt bereits geladene Werte anhand der ISIN.
 * Neue/geaenderte Positionen bekommen werteStatus 'idle' (Trigger fuers Nachladen),
 * fertige behalten ihre Werte – so flackern Gruppen-Aktionen nicht.
 */
const uebernehmeBasis = (state, payload) => {
  if (!payload) return;
  const alt = new Map(state.positionen.map((p) => [p.isin, p]));
  state.positionen = (payload.positionen ?? []).map((neu) => {
    const vorher = alt.get(neu.isin);
    if (!vorher || vorher.werteStatus !== 'done') {
      return { ...neu, werteStatus: 'idle' };
    }
    const erhalten = Object.fromEntries(WERTE_FELDER.map((f) => [f, vorher[f]]));
    return { ...neu, ...erhalten, werteStatus: 'done' };
  });
  state.gruppen = payload.gruppen ?? [];
};

/** Setzt den Nachlade-Status einer Position (per ISIN). */
const setzeWerteStatus = (state, isin, werteStatus) => {
  const p = state.positionen.find((x) => x.isin === isin);
  if (p) p.werteStatus = werteStatus;
};

const depotSlice = createSlice({
  name: 'depot',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDepot.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchDepot.fulfilled, (state, action) => {
        state.status = 'succeeded';
        uebernehmeBasis(state, action.payload);
      })
      .addCase(fetchDepot.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Werte einer Position: laden -> merken -> Fehler markieren.
      .addCase(fetchPositionWerte.pending, (state, action) => {
        setzeWerteStatus(state, action.meta.arg, 'loading');
      })
      .addCase(fetchPositionWerte.fulfilled, (state, action) => {
        const { isin, position } = action.payload;
        const i = state.positionen.findIndex((x) => x.isin === isin);
        if (i >= 0) state.positionen[i] = { ...position, werteStatus: 'done' };
      })
      .addCase(fetchPositionWerte.rejected, (state, action) => {
        setzeWerteStatus(state, action.meta.arg, 'error');
      })
      // CRUD-Aktionen liefern die Basisansicht; ein Nachkauf/Verkauf macht die
      // Werte der betroffenen Position ungueltig -> erneut nachladen.
      .addMatcher(
        (action) => action.type.startsWith('depot/') && action.type.endsWith('/fulfilled')
          && action.type !== fetchDepot.fulfilled.type
          && !action.type.startsWith('depot/fetchWerte'),
        (state, action) => {
          uebernehmeBasis(state, action.payload);
          if (action.type === addTransaktion.fulfilled.type) {
            setzeWerteStatus(state, action.meta.arg.isin, 'idle');
          }
        }
      );
  },
});

export const selectDepotPositionen = (state) => state.depot.positionen;
export const selectDepotGruppen = (state) => state.depot.gruppen;
export const selectDepotStatus = (state) => state.depot.status;

export default depotSlice.reducer;
